
import RcDomainObject from "@/domain/common/RcDomainObject.js";
import Company from "@/domain/model/company/Company.js";
import Employee from "@/domain/model/employee/Employee.js";
import InspectionMap from "@/domain/model/inspection/InspectionMap.js";
import Signature from "@/domain/model/user/Signature.js";
import User from "@/domain/model/user/User.js";
import RcDate from '@/domain/session/time/RcDate.js';
import StringUtils from '@/utils/StringUtils.js';
import TimesheetEntryMap from "./TimesheetEntryMap.js";
import TimesheetMap from "./TimesheetMap.js";
import RcTime from "../../session/time/RcTime.js";


export default class Timesheet extends RcDomainObject {

  static MODEL_NAME = "Timesheet";

  constructor(domain, data) {
    super(domain, data, Timesheet.MODEL_NAME);
  }

  static Fields = {
    COMPANY: Company.MODEL_NAME,
    EMPLOYEE: Employee.MODEL_NAME,
    USER: User.MODEL_NAME,
    ITEMS: TimesheetEntryMap.MODEL_NAME,
    INSPECTIONS: InspectionMap.MODEL_NAME,
    SIGNATURE: Signature.MODEL_NAME,
    YEAR: "year",
    MONTH: "month",
    DAY: "day",
    COMPLETED_DATE: "completedDate",
    CYCLE: "cycle",
    DATE: "date",
    TOTAL_OFF_DUTY: "offDuty",
    TOTAL_ON_DUTY: "onDuty",
    TOTAL_ON_DUTY_DRIVING: "totalOnDutyDriving",
    TOTAL_OFF_DUTY_AWAY: "totalOffDutyAway",
    TOTAL_OFF_DUTY_SLEEPER: "totalOffDutySleeper",
    TOTAL_ON_DUTY_NOT_DRIVING: "totalOnDutyNotDriving",
    LOCATION_START: "locationStart",
    LOCATION_END: "locationEnd",
    PDF_URL: "pdfUrl",
    SIGNED_BY: "signedBy",
  }

  static Values = {
    Status: {
      MISSING: 0,
      INPROGRESS: 1,
      INCOMPLETE: 2,
      COMPLETED: 3,
      COMPLETED_OFF_DAY: 4,
    },
  }

  //On Duty Not Driving
  totalOnDutyNotDrivingQuarters() {
    return this.getInt(Timesheet.Fields.TOTAL_ON_DUTY_NOT_DRIVING);
  }

  totalNotDrivingHours() {
    return this.totalOnDutyNotDrivingQuarters() / 4;
  }

  onDutyNotDrivingHoursDisplay() {
    return StringUtils.format("{0}", [this.totalNotDrivingHours()]);
  }

  //Off Duty Away
  totalOffDutyAwayQuarters() {
    return this.getInt(Timesheet.Fields.TOTAL_OFF_DUTY_AWAY);
  }

  totalOffDutyAwayHours() {
    return this.totalOffDutyAwayQuarters() / 4;
  }

  //On Duty Driving
  totalDrivingQuarters() {
    return this.getInt(Timesheet.Fields.TOTAL_ON_DUTY_DRIVING);
  }

  totalDrivingHours() {
    return this.totalDrivingQuarters() / 4;
  }

  onDutyDrivingHoursDisplay() {
    return StringUtils.format("{0}", [this.totalDrivingHours()]);
  }

  //On Duty both driving and working
  totalOnDutyQuarters() {
    return this.getInt(Timesheet.Fields.TOTAL_ON_DUTY);
  }

  totalOnDutyHours() {
    return this.totalOnDutyQuarters() / 4;
  }

  onDutyHoursDisplay() {
    return StringUtils.format("{0}", [this.totalOnDutyHours()]);
  }

  //Off Duty both away and sleeper
  totalOffDutyQuarters() {
    return this.getInt(Timesheet.Fields.TOTAL_OFF_DUTY);
  }

  totalOffDutyHours() {
    return this.totalOffDutyQuarters() / 4;
  }

  offDutyHoursDisplay() {
    return StringUtils.format("{0}", [this.totalOffDutyHours()]);
  }

  //Off and On Duty 
  totalHours() {
    var quarters = this.totalOnDutyQuarters() + this.totalOffDutyQuarters();
    return quarters / 4;
  }

  totalQuarters() {
    var quarters = this.totalOnDutyQuarters() + this.totalOffDutyQuarters();
    return quarters;
  }

  totalHoursDisplay() {
    return StringUtils.format("{0}", [this.totalHours()]);
  }

  fields() {
    return Timesheet.FIELDS;
  }

  copy() {
    var data = this._copy();
    return new Timesheet(this.domain, data);
  }

  totals() {
    var entries = this.entries().list();
    var uniqueEntries = {};
    entries.forEach(function (entry) {
      uniqueEntries[entry.timeIdStart()] = entry;
    });

    var uniqueList = Object.values(uniqueEntries);
    var entryMap = new TimesheetEntryMap(this.domain, {});
    uniqueList.forEach(function (entry) {
      entryMap.add(entry);
    });
    this.put(Timesheet.Fields.ITEMS, entryMap);

    var onDutyNotDriving = 0;
    var onDutyDriving = 0;
    var offDutyAway = 0;
    var offDutySleeper = 0;

    uniqueList.forEach(function (entry) {
      var quarters = entry.totalQuarters();
      if (entry.isOnDuty()) {
        onDutyNotDriving += quarters;
      } else if (entry.isOnDutyDriving()) {
        onDutyDriving += quarters;
      } else if (entry.isOffDutySleeper()) {
        offDutySleeper += quarters;
      } else if (entry.isOffDuty()) {
        offDutyAway += quarters;
      }
    });

    var onDuty = onDutyDriving + onDutyNotDriving;
    var offDuty = offDutyAway + offDutySleeper;

    this.put(Timesheet.Fields.TOTAL_ON_DUTY_NOT_DRIVING, onDutyNotDriving);
    this.put(Timesheet.Fields.TOTAL_ON_DUTY_DRIVING, onDutyDriving);
    this.put(Timesheet.Fields.TOTAL_ON_DUTY, onDuty);
    this.put(Timesheet.Fields.TOTAL_OFF_DUTY_AWAY, offDutyAway);
    this.put(Timesheet.Fields.TOTAL_OFF_DUTY_SLEEPER, offDutySleeper);
    this.put(Timesheet.Fields.TOTAL_OFF_DUTY, offDuty);

    return this;
  }

  signature() {
    var data = this.data[Timesheet.Fields.SIGNATURE];
    return new Signature(this.domain, data);
  }

  withSignature(signature){
    this.put(Timesheet.Fields.SIGNATURE, signature);
    return this;
  }
  
  company() {
    var companyData = this.data[Timesheet.Fields.COMPANY];
    return new Company(this.domain, companyData);
  }

  entries() {
    var data = this.data[Timesheet.Fields.ITEMS];
    return new TimesheetEntryMap(this.domain, data);
  }

  inspections() {
    var data = this.data[Timesheet.Fields.INSPECTIONS];
    return new InspectionMap(this.domain, data);
  }

  withDate(rcDate) {
    this.put(Timesheet.Fields.YEAR, rcDate.year().value());
    this.put(Timesheet.Fields.MONTH, rcDate.month().value());
    this.put(Timesheet.Fields.DAY, rcDate.day().value());
    this.put(Timesheet.Fields.DATE, rcDate.day().firstHour().time());
    return this;
  }

  withCompletedDateNow() {
    this.put(Timesheet.Fields.COMPLETED_DATE, RcTime.time().now().time());
    return this;
  }

  year() {
    return this.getInt(Timesheet.Fields.YEAR);
  }

  month() {
    return this.getInt(Timesheet.Fields.MONTH);
  }

  day() {
    return this.getInt(Timesheet.Fields.DAY);
  }

  dateDisplay() {
    var theYear = StringUtils.toString(this.year());
    var theMonth = StringUtils.toString(this.month() + 1);
    var theDay = StringUtils.toString(this.day() + 1);
    if (StringUtils.isEmpty(theYear)) {
      return "";
    }
    return StringUtils.format("{0}-{1}-{2}", [theYear, theMonth, theDay]);
  }

  date() {
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(this.get(Timesheet.Fields.DATE)), timeZone);
    return rcDate;
  }

  completedDate() {
    return this.get(Timesheet.Fields.COMPLETED_DATE);
  }

  completedDateDisplay() {
    var completedDate = this.completedDate();
    if (!completedDate) {
      return "";
    }
    var timeZone = this.timeZone();
    var rcDate = new RcDate(new Date(completedDate), timeZone);
    return rcDate.displayAs_YYYY_MM_DD_HH_MM_SS_A();
  }

  cycle() {
    return this.get(Timesheet.Fields.CYCLE);
  }

  locationStart() {
    return this.getString(Timesheet.Fields.LOCATION_START);
  }

  hasStartLocation() {
    return StringUtils.isNotEmpty(this.locationStart());
  }

  locationEnd() {
    return this.getString(Timesheet.Fields.LOCATION_END);
  }

  hasEndLocation() {
    return StringUtils.isNotEmpty(this.locationEnd());
  }

  cycleDisplay() {
    if (this.cycle() == 2) {
      return "Cycle 2 (14 day)";
    }
    return "Cycle 1 (7 day)";
  }

  isToday() {
    var now = this.domain.session().time().now().day().firstHour().day();
    // day(), month() and day() are all stored zero based, and RcTime.day() takes a zero
    // based day of the month, so the stored parts go in as they are. Adding one here read
    // every timesheet as the day after itself, which made today's sheet look like a past
    // day and yesterday's the one still in progress.
    var rcDate = this.domain.session().time().day(this.year(), this.month(), this.day()).firstHour().day();
    var result = rcDate.time() == now.time();
    return result;
  }

  statusDisplay() {
    if (this.isStatusComplete() || this.isStatusCompleteOffDay()) {
      return "Complete";
    }
    if (this.isStatusInProgress()) {
      return "In Progress";
    }

    if (this.isStatusIncomplete()) {
      return "Incomplete";
    }

    return "Missing Hours";
  }

  status() {
    var quarters = this.totalQuarters();
    var date = this.completedDate();

    if (date > 0 && quarters == 96) {
      let newSignature = this.signature().isNew();
      if (this.hasStartLocation() && this.hasEndLocation() && !newSignature) {
        return Timesheet.Values.Status.COMPLETED;
      }
      let totalOffDuty = this.totalOffDutyQuarters();
      if (totalOffDuty == 96 && !newSignature) {
        return Timesheet.Values.Status.COMPLETED_OFF_DAY;
      }
    }

    // Past this point the day is not complete. Today's is still being filled in, whatever
    // is missing from it, so it is in progress rather than a compliance gap; only an older
    // day that never got finished is incomplete.
    if (this.isToday()) {
      return Timesheet.Values.Status.INPROGRESS;
    }

    if (quarters == 96 || this.isNotNew()) {
      return Timesheet.Values.Status.INCOMPLETE;
    }

    return Timesheet.Values.Status.MISSING;
  }

  defectType() {
    if (this.isStatusComplete()) {
      return "Complete";
    }

    if (this.isStatusCompleteOffDay()) {
      return "Complete";
    }

    if (this.isStatusInProgress()) {
      return "In Progress";
    }

    if (this.isStatusIncomplete()) {
      return "Incomplete";
    }

    return "Missing";
  }

  isStatusInProgress() {
    var status = this.status();
    return status == Timesheet.Values.Status.INPROGRESS;
  }

  isStatusComplete() {
    return this.status() == Timesheet.Values.Status.COMPLETED;
  }

  isStatusCompleteOffDay() {
    return this.status() == Timesheet.Values.Status.COMPLETED_OFF_DAY;
  }

  isStatusIncomplete() {
    return this.status() == Timesheet.Values.Status.INCOMPLETE;
  }

  isStatusMissing() {
    return this.status() == Timesheet.Values.Status.MISSING;
  }

  pdfUrl() {
    var host = this.domain.client().cdnHost;
    var url = host + this.getString(Timesheet.Fields.PDF_URL);
    return url;
  }

  hasPdfUrl() {
    return !StringUtils.isEmpty(this.getString(Timesheet.Fields.PDF_URL));
  }

  map() {
    var map = new TimesheetMap(this.domain);
    map.add(this);
    return map;
  }

  createdBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.CREATED_BY));
  }

  modifiedBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.MODIFIED_BY));
  }

  withEmployee(employee) {
    this.put(Timesheet.Fields.EMPLOYEE, employee);
    return this.withUser(employee.user());
  }

  employee() {
    var employeeData = this.data[Timesheet.Fields.EMPLOYEE];
    return new Employee(this.domain, employeeData);
  }

  withUser(user) {
    this.put(Timesheet.Fields.USER, user);
    return this;
  }

  user() {
    return new User(this.domain, this.get(Timesheet.Fields.USER));
  }

  signedBy() {
    return new User(this.domain, this.get(Timesheet.Fields.SIGNED_BY));
  }

  withSignedBy(user) {
    this.put(Timesheet.Fields.SIGNED_BY, user);
    return this;
  }
}